import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { SlidersHorizontal, Plus, Edit, Trash2, Loader2, RefreshCw, Search } from "lucide-react";
import type { SettingRuleRecord } from "@/types";

const VALUE_TYPES = ["STRING", "NUMBER", "BOOLEAN", "JSON"] as const;
type ValueType = (typeof VALUE_TYPES)[number];
const VALUE_TYPE_LABELS: Record<ValueType, string> = { STRING: "Text", NUMBER: "Number", BOOLEAN: "On / off", JSON: "List or table (advanced)" };

const RULE_GROUPS = ["GENERAL", "QUOTE", "SLA", "ASSIGNMENT", "VALIDATION", "PIPELINE", "PROPOSAL", "SECURITY"];

const EMPTY_FORM = {
  rule_key: "",
  rule_name: "",
  rule_group: "GENERAL",
  value_type: "STRING" as ValueType,
  value: "",
  description: "",
};

const selectClass =
  "h-9 sm:h-10 w-full rounded-md border border-border bg-background px-3 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary";

/** Render a stored value in the editor as text. */
function valueToText(value: any, type: string): string {
  if (value === null || value === undefined) return "";
  if (type === "JSON") return JSON.stringify(value, null, 2);
  return String(value);
}

/** Parse editor text into the typed value the backend stores; throws on invalid input. */
function textToValue(text: string, type: ValueType): any {
  switch (type) {
    case "NUMBER": {
      const n = Number(text);
      if (text.trim() === "" || Number.isNaN(n)) throw new Error("Value must be a valid number");
      return n;
    }
    case "BOOLEAN":
      return text === "true";
    case "JSON":
      try {
        return JSON.parse(text);
      } catch {
        throw new Error("This value isn't written correctly. Check the brackets, quotes and commas.");
      }
    default:
      return text;
  }
}

function displayValue(rule: SettingRuleRecord) {
  if (rule.value_type === "BOOLEAN") {
    return rule.value_json ? (
      <Badge variant="success">Enabled</Badge>
    ) : (
      <Badge variant="secondary">Disabled</Badge>
    );
  }
  if (rule.value_type === "JSON") {
    return (
      <code className="px-2 py-0.5 rounded bg-muted font-mono text-xs truncate max-w-[220px] inline-block align-middle">
        {JSON.stringify(rule.value_json)}
      </code>
    );
  }
  return (
    <span className="font-semibold text-sm text-foreground">
      {valueToText(rule.value_json, rule.value_type) || "-"}
    </span>
  );
}

export function SettingRulePage() {
  const [rules, setRules] = useState<SettingRuleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("ALL");
  const [actionLoading, setActionLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<SettingRuleRecord | null>(null);
  const [deleteItem, setDeleteItem] = useState<SettingRuleRecord | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const { success, error } = useToast();

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/SettingRule/List");
      setRules(unwrapArray<SettingRuleRecord>(res));
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load setting rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const groups = Array.from(new Set(rules.map((r) => r.rule_group))).sort();

  const filtered = rules.filter((r) => {
    const q = search.toLowerCase();
    const matchesSearch =
      r.rule_key.toLowerCase().includes(q) || r.rule_name.toLowerCase().includes(q);
    const matchesGroup = groupFilter === "ALL" || r.rule_group === groupFilter;
    return matchesSearch && matchesGroup;
  });

  const buildPayload = () => ({
    rule_key: form.rule_key.trim().toUpperCase(),
    rule_name: form.rule_name.trim(),
    rule_group: form.rule_group,
    value_type: form.value_type,
    value_json: textToValue(form.value, form.value_type),
    description: form.description.trim() || undefined,
  });

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.rule_key.trim() || !form.rule_name.trim()) {
      error("Validation Error", "Rule key and name are required");
      return;
    }

    let payload;
    try {
      payload = buildPayload();
    } catch (err: any) {
      error("Validation Error", err.message);
      return;
    }

    setActionLoading(true);
    try {
      await api.post("/api/SettingRule/Insert", payload);
      success("Rule Created", "Setting rule created successfully");
      setAddOpen(false);
      setForm(EMPTY_FORM);
      await loadData();
    } catch (err: any) {
      error("Creation Failed", err.message || "Failed to create rule");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditOpen = (r: SettingRuleRecord) => {
    const type = (VALUE_TYPES as readonly string[]).includes(r.value_type)
      ? (r.value_type as ValueType)
      : "STRING";
    setEditItem(r);
    setForm({
      rule_key: r.rule_key,
      rule_name: r.rule_name,
      rule_group: r.rule_group,
      value_type: type,
      value: type === "BOOLEAN" ? String(!!r.value_json) : valueToText(r.value_json, type),
      description: r.description || "",
    });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem) return;

    let payload;
    try {
      payload = buildPayload();
    } catch (err: any) {
      error("Validation Error", err.message);
      return;
    }

    setActionLoading(true);
    try {
      await api.put(`/api/SettingRule/Update/${editItem.id}`, payload);
      success("Rule Updated", "Setting rule saved successfully");
      setEditItem(null);
      setForm(EMPTY_FORM);
      await loadData();
    } catch (err: any) {
      error("Update Failed", err.message || "Failed to update rule");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/SettingRule/Delete/${deleteItem.id}`);
      success("Rule Deleted", "Setting rule deleted successfully");
      setDeleteItem(null);
      await loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete rule");
    } finally {
      setActionLoading(false);
    }
  };

  const renderFormFields = (isEdit: boolean) => (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs sm:text-sm font-semibold">Rule Key *</Label>
          <Input
            required
            placeholder="QUOTE_VALIDITY_DAYS"
            value={form.rule_key}
            disabled={isEdit}
            onChange={(e) => setForm({ ...form, rule_key: e.target.value })}
            className="font-mono uppercase"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs sm:text-sm font-semibold">Group</Label>
          <SearchableSelect
            value={form.rule_group}
            onChange={(e) => setForm({ ...form, rule_group: e.target.value })}
            className={selectClass}
          >
            {Array.from(new Set([...RULE_GROUPS, form.rule_group])).map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </SearchableSelect>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs sm:text-sm font-semibold">Rule Name *</Label>
        <Input
          required
          placeholder="Max Sales Rep Discount"
          value={form.rule_name}
          onChange={(e) => setForm({ ...form, rule_name: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs sm:text-sm font-semibold">Value Type</Label>
          <SearchableSelect
            value={form.value_type}
            onChange={(e) => {
              const value_type = e.target.value as ValueType;
              setForm({ ...form, value_type, value: value_type === "BOOLEAN" ? "false" : "" });
            }}
            className={selectClass}
          >
            {VALUE_TYPES.map((t) => (
              <option key={t} value={t}>
                {VALUE_TYPE_LABELS[t]}
              </option>
            ))}
          </SearchableSelect>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-xs sm:text-sm font-semibold">Value</Label>
          {form.value_type === "BOOLEAN" ? (
            <div className="flex items-center h-9 sm:h-10 gap-2.5">
              <Switch
                checked={form.value === "true"}
                onCheckedChange={(checked) => setForm({ ...form, value: String(checked) })}
              />
              <span className="text-xs sm:text-sm text-muted-foreground">
                {form.value === "true" ? "Enabled" : "Disabled"}
              </span>
            </div>
          ) : form.value_type === "JSON" ? (
            <textarea
              rows={4}
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              placeholder='{"key": "value"}'
              className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs sm:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          ) : (
            <Input
              type={form.value_type === "NUMBER" ? "number" : "text"}
              step="any"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
          )}
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs sm:text-sm font-semibold">Description</Label>
        <Input
          placeholder="What this rule controls"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
    </>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">Business Rules</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Configure discount limits, SLA thresholds, lead assignment and validation rules
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="h-9 gap-1.5"
          >
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setForm(EMPTY_FORM);
              setAddOpen(true);
            }}
            className="h-9 gap-1.5 shadow-sm"
          >
            <Plus className="size-4" />
            Add Rule
          </Button>
        </div>
      </div>

      {/* Search + group filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
          <Input
            placeholder="Search rules by key or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
        <SearchableSelect
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          className={`${selectClass} sm:w-48`}
        >
          <option value="ALL">All Groups</option>
          {groups.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </SearchableSelect>
      </div>

      {/* Table */}
      {loading ? (
        <TableSkeleton rows={6} columns={4} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rule</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Value</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="flex items-start gap-2.5">
                      <div className="size-8 rounded-lg bg-primary/10 text-accent-foreground flex items-center justify-center shrink-0">
                        <SlidersHorizontal className="size-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-foreground">{r.rule_name}</div>
                        <code className="font-mono text-[11px] text-muted-foreground">
                          {r.rule_key}
                        </code>
                        {r.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                            {r.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{r.rule_group}</Badge>
                  </TableCell>
                  <TableCell>{displayValue(r)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => handleEditOpen(r)}
                      title="Edit Rule"
                    >
                      <Edit className="size-4 text-muted-foreground hover:text-foreground" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setDeleteItem(r)}
                      title="Delete Rule"
                    >
                      <Trash2 className="size-4 text-danger hover:text-danger" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-10 text-muted-foreground text-sm">
                    No setting rules found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Dialog: Add */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Add Rule</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">Create a new configurable business rule</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4 py-2">
            {renderFormFields(false)}
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Rule
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit */}
      <Dialog open={!!editItem} onOpenChange={(open) => !open && setEditItem(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Edit Rule</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Changes take effect immediately for all users
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 py-2">
            {renderFormFields(true)}
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditItem(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Delete */}
      <Dialog open={!!deleteItem} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg text-danger">Delete Rule</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Are you sure you want to remove{" "}
              <strong className="text-foreground">{deleteItem?.rule_key}</strong>? Features that
              read this rule will fall back to their built-in default.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteItem(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
